import { useState, useEffect } from 'react';
import { Table, Empty, Tabs, Tag } from 'antd';
import { FileTextOutlined, WalletOutlined, PlusCircleOutlined, GiftOutlined } from '@ant-design/icons';
import { billsAPI, ordersAPI, referralAPI } from '../services/api';
import { t } from '../utils/i18n';
import dayjs from 'dayjs';

const Bills = () => {
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bonusRecords, setBonusRecords] = useState([]);
  const [billsTotal, setBillsTotal] = useState(0);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [bonusTotal, setBonusTotal] = useState(0);
  const [billsPage, setBillsPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);
  const [bonusPage, setBonusPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [activeTab, setActiveTab] = useState('bills');
  // 统计数据（从后端获取）
  const [totalConsumption, setTotalConsumption] = useState(0);
  const [totalRecharge, setTotalRecharge] = useState(0);
  const [onlineRecharge, setOnlineRecharge] = useState(0);
  const [offlineRecharge, setOfflineRecharge] = useState(0);
  const [totalBonus, setTotalBonus] = useState(0);

  useEffect(() => {
    if (activeTab === 'bills') {
      fetchBills();
    }
  }, [billsPage, pageSize, activeTab]);

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
    }
  }, [ordersPage, pageSize, activeTab]);

  useEffect(() => {
    if (activeTab === 'bonus') {
      fetchBonusRecords();
    }
  }, [bonusPage, pageSize, activeTab]);

  const fetchBills = async () => {
    setLoading(true);
    try {
      const response = await billsAPI.getList(billsPage, pageSize);
      if (response.code === 0) {
        setBills(response.data.list || []);
        setBillsTotal(response.data.total || 0);
        setTotalConsumption(response.data.total_consumption || 0);
      }
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await ordersAPI.getList(ordersPage, pageSize);
      if (response.code === 0) {
        setOrders(response.data.orders || []);
        setOrdersTotal(response.data.total || 0);
        setTotalRecharge(response.data.total_amount || 0);
        setOnlineRecharge(response.data.online_amount || 0);
        setOfflineRecharge(response.data.offline_amount || 0);
      }
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchBonusRecords = async () => {
    setLoading(true);
    try {
      const response = await referralAPI.getRecords();
      if (response.code === 0) {
        const campaignRecords = response.data?.campaign_records || [];
        const merchantRecords = response.data?.merchant_referral_records || [];
        
        // 转换商户记录格式以匹配表格列
        const formattedMerchantRecords = merchantRecords.map(r => ({
          id: `m_${r.id}`,
          created_at: r.created_at,
          referee_email: r.referee_email,
          bonus_amount: r.amount,
          status: r.status,
        }));
        
        const allRecords = [...campaignRecords, ...formattedMerchantRecords];
        // 按时间排序
        allRecords.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setBonusRecords(allRecords);
        setBonusTotal(allRecords.length);
        // 计算总奖励
        const total = allRecords.reduce((sum, r) => sum + (r.bonus_amount || 0), 0);
        setTotalBonus(total);
      }
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const billColumns = [
    {
      title: t('model') || 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 150,
      align: 'center',
      render: (model) => (
        <span style={{ fontFamily: 'SF Mono, monospace', fontSize: 13 }}>
          {model || '-'}
        </span>
      ),
    },
    {
      title: t('input_tokens') || 'Input',
      dataIndex: 'input_tokens',
      key: 'input_tokens',
      width: 100,
      align: 'center',
      render: (tokens) => (
        <span style={{ color: '#6b7280', fontSize: 13 }}>{tokens || 0}</span>
      ),
    },
    {
      title: t('output_tokens') || 'Output',
      dataIndex: 'output_tokens',
      key: 'output_tokens',
      width: 100,
      align: 'center',
      render: (tokens) => (
        <span style={{ color: '#6b7280', fontSize: 13 }}>{tokens || 0}</span>
      ),
    },
    {
      title: t('balance_before') || 'Balance Before',
      dataIndex: 'balance_before',
      key: 'balance_before',
      width: 120,
      align: 'center',
      render: (balance) => (
        <span style={{ color: '#6b7280', fontSize: 13 }}>
          ${parseFloat(balance || 0).toFixed(4)}
        </span>
      ),
    },
    {
      title: t('amount') || 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'center',
      render: (amount) => (
        <span style={{ fontSize: 14, fontWeight: 600, color: '#dc2626' }}>
          -${parseFloat(amount || 0).toFixed(6)}
        </span>
      ),
    },
    {
      title: t('balance_after') || 'Balance After',
      dataIndex: 'balance_after',
      key: 'balance_after',
      width: 120,
      align: 'center',
      render: (balance) => (
        <span style={{ color: '#16a34a', fontSize: 13 }}>
          ${parseFloat(balance || 0).toFixed(4)}
        </span>
      ),
    },
    {
      title: t('created_at') || 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      align: 'center',
      render: (time) =>
        time ? (
          <span style={{ color: '#6b7280', fontSize: 13 }}>{dayjs(time).format('YYYY-MM-DD HH:mm:ss')}</span>
        ) : (
          '-'
        ),
    },
  ];

  // 充值记录列
  const orderColumns = [
    {
      title: t('order_no'),
      dataIndex: 'order_no',
      key: 'order_no',
      width: 200,
      align: 'center',
      render: (orderNo, record) => (
        <span style={{ fontFamily: 'SF Mono, monospace', fontSize: 12, color: '#3b82f6' }}>
          {orderNo || `#${record.id}`}
        </span>
      ),
    },
    {
      title: t('type'),
      key: 'recharge_type',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const isOnline = record.recharge_type === 'online' || record.payment_method === 'stripe' || record.payment_method === 'paypal';
        return (
          <span style={{ 
            color: isOnline ? '#3b82f6' : '#8b5cf6',
            fontSize: 13,
            fontWeight: 500
          }}>
            {isOnline ? t('online') : t('offline')}
          </span>
        );
      },
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'center',
      render: (amount) => (
        <span style={{ fontSize: 14, fontWeight: 600, color: '#16a34a' }}>
          +${parseFloat(amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: t('balance_before'),
      dataIndex: 'balance_before',
      key: 'balance_before',
      width: 120,
      align: 'center',
      render: (balance) => (
        <span style={{ color: '#6b7280', fontSize: 13 }}>
          ${parseFloat(balance || 0).toFixed(4)}
        </span>
      ),
    },
    {
      title: t('balance_after'),
      dataIndex: 'balance_after',
      key: 'balance_after',
      width: 120,
      align: 'center',
      render: (balance) => (
        <span style={{ color: '#16a34a', fontSize: 13 }}>
          ${parseFloat(balance || 0).toFixed(4)}
        </span>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (status) => {
        const statusMap = {
          pending: { color: 'orange', text: t('pending') },
          succeeded: { color: 'green', text: t('succeeded') },
          failed: { color: 'red', text: t('failed') },
          cancelled: { color: '#faad14', text: t('cancelled') },
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <span style={{ color, fontSize: 13 }}>{text}</span>;
      },
    },
    {
      title: t('method'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const method = record.payment_method;
        const methodMap = {
          stripe: { color: '#635bff', name: 'Stripe' },
          paypal: { color: '#003087', name: 'PayPal' },
          fastspring: { color: '#ff6600', name: 'FastSpring' },
        };
        const info = methodMap[method];
        if (info) {
          return (
            <Tag color={info.color} style={{ fontSize: 12 }}>
              {info.name}
            </Tag>
          );
        }
        return <span style={{ color: '#8b5cf6', fontSize: 13 }}>{t('manual')}</span>;
      },
    },
    {
      title: t('time'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      align: 'center',
      render: (time) =>
        time ? (
          <span style={{ color: '#6b7280', fontSize: 13 }}>{dayjs(time).format('YYYY-MM-DD HH:mm:ss')}</span>
        ) : (
          '-'
        ),
    },
  ];

  // 奖励记录列
  const bonusColumns = [
    {
      title: t('time'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      align: 'center',
      render: (time) =>
        time ? (
          <span style={{ color: '#6b7280', fontSize: 13 }}>{dayjs(time).format('YYYY-MM-DD HH:mm:ss')}</span>
        ) : (
          '-'
        ),
    },
    {
      title: t('referee_email'),
      dataIndex: 'referee_email',
      key: 'referee_email',
      width: 200,
      render: (email) => (
        <span style={{ fontSize: 13 }}>{email || '-'}</span>
      ),
    },
    {
      title: t('bonus_amount'),
      dataIndex: 'bonus_amount',
      key: 'bonus_amount',
      width: 120,
      align: 'center',
      render: (amount) => (
        <span style={{ fontSize: 14, fontWeight: 600, color: '#16a34a' }}>
          +${parseFloat(amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status) => {
        const statusMap = {
          completed: { color: 'green', text: t('completed') },
          pending: { color: 'orange', text: t('pending') },
          cancelled: { color: 'red', text: t('cancelled') },
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  // 直接使用后端返回的订单数据
  const orderRecords = orders;

  const tabItems = [
    {
      key: 'bills',
      label: t('consumption_records') || 'Consumption',
      children: (
        <>
          <div className="table-header-bar">
            <div className="table-stat">
              <WalletOutlined className="table-stat-icon" />
              <div>
                <span className="table-stat-label">{t('total_consumption') || 'Total Consumption'}</span>
                <span className="table-stat-value" style={{ color: '#dc2626' }}>
                  ${totalConsumption.toFixed(4)}
                </span>
              </div>
            </div>
            <span className="table-record-count">{t('total_records') || 'Total'} {billsTotal} {t('records') || 'records'}</span>
          </div>
          <div className="table-wrapper">
            {billsTotal === 0 && !loading ? (
              <Empty
                description={<span style={{ color: '#6b7280' }}>{t('no_consumption_records') || 'No consumption records'}</span>}
                style={{ padding: 60 }}
              />
            ) : (
              <Table
                columns={billColumns}
                dataSource={bills}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: billsPage,
                  pageSize: pageSize,
                  total: billsTotal,
                  showSizeChanger: false,
                  showTotal: (total) => `${total} ${total === 1 ? (t('record') || 'record') : (t('records') || 'records')}`,
                  onChange: (p, ps) => {
                    setBillsPage(p);
                    setPageSize(ps);
                  },
                }}
              />
            )}
          </div>
        </>
      ),
    },
    {
      key: 'orders',
      label: t('recharge_records') || 'Recharge',
      children: (
        <>
          <div className="table-header-bar">
            <div className="table-stat">
              <PlusCircleOutlined className="table-stat-icon" style={{ color: '#16a34a' }} />
              <div>
                <span className="table-stat-label">{t('total_recharge') || 'Total Recharge'}</span>
                <span className="table-stat-value" style={{ color: '#16a34a' }}>
                  ${totalRecharge.toFixed(2)}
                </span>
              </div>
            </div>
            <div className="table-stat" style={{ marginLeft: 24 }}>
              <PlusCircleOutlined className="table-stat-icon" style={{ color: '#3b82f6' }} />
              <div>
                <span className="table-stat-label">{t('online')}</span>
                <span className="table-stat-value" style={{ color: '#3b82f6' }}>
                  ${onlineRecharge.toFixed(2)}
                </span>
              </div>
            </div>
            <div className="table-stat" style={{ marginLeft: 24 }}>
              <PlusCircleOutlined className="table-stat-icon" style={{ color: '#8b5cf6' }} />
              <div>
                <span className="table-stat-label">{t('offline')}</span>
                <span className="table-stat-value" style={{ color: '#8b5cf6' }}>
                  ${offlineRecharge.toFixed(2)}
                </span>
              </div>
            </div>
            <span className="table-record-count">{t('total_records') || 'Total'} {ordersTotal} {t('records') || 'records'}</span>
          </div>
          <div className="table-wrapper">
            {ordersTotal === 0 && !loading ? (
              <Empty
                description={<span style={{ color: '#6b7280' }}>{t('no_recharge_records') || 'No recharge records'}</span>}
                style={{ padding: 60 }}
              />
            ) : (
              <Table
                columns={orderColumns}
                dataSource={orderRecords}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: ordersPage,
                  pageSize: pageSize,
                  total: ordersTotal,
                  showSizeChanger: false,
                  showTotal: (total) => `${total} ${total === 1 ? (t('record') || 'record') : (t('records') || 'records')}`,
                  onChange: (p, ps) => {
                    setOrdersPage(p);
                    setPageSize(ps);
                  },
                }}
              />
            )}
          </div>
        </>
      ),
    },
    {
      key: 'bonus',
      label: t('bonus_records'),
      children: (
        <>
          <div className="table-header-bar">
            <div className="table-stat">
              <GiftOutlined className="table-stat-icon" style={{ color: '#f59e0b' }} />
              <div>
                <span className="table-stat-label">{t('total_bonus')}</span>
                <span className="table-stat-value" style={{ color: '#f59e0b' }}>
                  ${totalBonus.toFixed(2)}
                </span>
              </div>
            </div>
            <span className="table-record-count">{t('total_records')} {bonusTotal} {t('records')}</span>
          </div>
          <div className="table-wrapper">
            {bonusTotal === 0 && !loading ? (
              <Empty
                description={<span style={{ color: '#6b7280' }}>{t('no_bonus_records')}</span>}
                style={{ padding: 60 }}
              />
            ) : (
              <Table
                columns={bonusColumns}
                dataSource={bonusRecords}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: bonusPage,
                  pageSize: pageSize,
                  total: bonusTotal,
                  showSizeChanger: false,
                  showTotal: (total) => `${total} ${total === 1 ? t('record') : t('records')}`,
                  onChange: (p, ps) => {
                    setBonusPage(p);
                    setPageSize(ps);
                  },
                }}
              />
            )}
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="user-page-wrapper">
      <div className="user-page-container">
        {/* 页面标题 */}
        <div className="page-title">
          <div className="title-icon">
            <FileTextOutlined />
          </div>
          {t('transaction_records')}
        </div>

        {/* Tab切换 */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ marginTop: 16 }}
        />
      </div>
    </div>
  );
};

export default Bills;
